import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Grid, Paper, Alert,
} from '@mui/material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { useDataProvider } from 'react-admin';
import { buildTransitionChain } from '@/components/workflow/machineHelpers';
import { useRuleEvaluation } from '@/components/workflow/useRuleEvaluation';

const STEPS = [
  { id: 'client-services', title: 'Client & Services' },
  { id: 'validity', title: 'Validity Period' },
  { id: 'approval', title: 'Approval' },
  { id: 'review', title: 'Review & Submit' },
];

interface FormValues {
  clientId: string;
  clientName: string;
  services: Array<{ serviceId: string; serviceName: string; rate: number }>;
  effectiveFrom: string;
  effectiveTo: string;
  maxSessions: number;
  approverId: string;
  approvalNotes: string;
  notes: string;
}

const INITIAL_VALUES: FormValues = {
  clientId: '',
  clientName: '',
  services: [],
  effectiveFrom: '',
  effectiveTo: '',
  maxSessions: 0,
  approverId: '',
  approvalNotes: '',
  notes: '',
};

const stepDescriptions: Record<string, string> = {
  'client-services': "Choose the service and client for this authorisation. Authorisations approve tutoring services for specific clients.",
  validity: "Set the approved hours, validity period, and any conditions.",
  approval: "Assign an approver and add approval notes.",
  review: "Review the authorisation before creating.",
};

export const ServiceAuthorisationWizard = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const evaluateRef = useRef<(() => Promise<{ allowed: boolean; reason?: string }>) | null>(null);

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    if (evaluateRef.current) {
      const ruleResult = await evaluateRef.current();
      if (!ruleResult.allowed) {
        throw new Error(ruleResult.reason || 'Rule evaluation failed');
      }
    }

    const entityResult = await dataProvider.create('tutoring-service-authorisations', {
      data: {
        clientId: v.clientId,
        clientName: v.clientName,
        services: v.services,
        effectiveFrom: v.effectiveFrom,
        effectiveTo: v.effectiveTo,
        maxSessions: v.maxSessions,
        approverId: v.approverId,
        approvalNotes: v.approvalNotes,
        notes: v.notes,
        status: 'PendingApproval',
      },
    });

    const entityId = entityResult.data.id;
    const { finalState, transitions } = buildTransitionChain('service-authorisation', 'draft', ['SUBMIT_FOR_APPROVAL']);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'service-authorisation',
        entityId,
        entityName: `Auth ${v.clientName}`,
        currentState: { value: finalState, context: v },
        status: 'active',
        transitions,
      },
    });

    return { id: entityId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback,
    nextStep, prevStep, updateValues, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
    stepValidators: {
      'client-services': (vals) => {
        const v = vals as unknown as FormValues;
        const errs: Record<string, string> = {};
        if (!v.clientId?.trim()) errs.clientId = 'Client is required';
        return { isValid: Object.keys(errs).length === 0, errors: errs };
      },
    },
  });

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const vals = values as unknown as FormValues;
  const { evaluate } = useRuleEvaluation({ ruleId: 'service-authorisation:create', context: vals as unknown as Record<string, unknown> });
  useEffect(() => {
    evaluateRef.current = evaluate;
  }, [evaluate]);

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'client-services':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Client ID" value={vals.clientId} onChange={e => updateField('clientId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Client Name" value={vals.clientName} onChange={e => updateField('clientName', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Notes" value={vals.notes} onChange={e => updateField('notes', e.target.value)} fullWidth multiline rows={2} />
            </Grid>
          </Grid>
        );
      case 'validity':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Effective From" type="date" value={vals.effectiveFrom} onChange={e => updateField('effectiveFrom', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Effective To" type="date" value={vals.effectiveTo} onChange={e => updateField('effectiveTo', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Max Sessions" type="number" value={vals.maxSessions} onChange={e => updateField('maxSessions', parseInt(e.target.value) || 0)} fullWidth inputProps={{ min: 0 }} />
            </Grid>
          </Grid>
        );
      case 'approval':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Approver ID" value={vals.approverId} onChange={e => updateField('approverId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Approval Notes" value={vals.approvalNotes} onChange={e => updateField('approvalNotes', e.target.value)} fullWidth multiline rows={3} />
            </Grid>
          </Grid>
        );
      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Review authorisation details before creating. A workflow will be initialized for approval.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Authorisation Info</Typography>
                <Typography variant="body2"><strong>Client:</strong> {vals.clientName}</Typography>
                <Typography variant="body2"><strong>Period:</strong> {vals.effectiveFrom} to {vals.effectiveTo}</Typography>
                <Typography variant="body2"><strong>Max Sessions:</strong> {vals.maxSessions}</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Approval</Typography>
                <Typography variant="body2"><strong>Approver:</strong> {vals.approverId}</Typography>
                <Typography variant="body2"><strong>Notes:</strong> {vals.approvalNotes}</Typography>
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Service Authorisation Created"
          processId={(values as { id: string }).id}
          fields={[
            { label: 'Client', value: vals.clientName },
            { label: 'Period', value: `${vals.effectiveFrom} to ${vals.effectiveTo}` },
            { label: 'Approver', value: vals.approverId },
          ]}
          footerActions={[
            { label: 'View Authorisations', to: '/tutoring-service-authorisations' },
            { label: 'Create Another', onClick: reset },
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={currentStep.title}
          description={stepDescriptions[currentStep.id]}
        >
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          {renderFormFields()}
        </WizardStep>
      )}
    </WizardLayout>
  );
};
